import React, { useEffect, useState } from "react";
import { collection, getDocs, query, where, updateDoc, doc } from "firebase/firestore";
import { db } from "../../firebase";

interface User {
  uid: string;
  name: string;
  class: string;
  batchId: string;
  batchName: string;
  phoneNumber?: string;
}

const classOptions = ["IX", "X", "XI", "XII"];
const monthOptions = [
  "January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"
];

interface Batch {
  id: string;
  name: string;
  class: string;
  schedule: string;
  curriculum: string;
  location: string;
}

const Payment: React.FC = () => {
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedBatch, setSelectedBatch] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [batches, setBatches] = useState<Batch[]>([]);
  const [students, setStudents] = useState<User[]>([]);
  const [paidStatus, setPaidStatus] = useState<{ [uid: string]: boolean }>({});
  const [loading, setLoading] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMsg, setToastMsg] = useState("");

  // Fetch all batches
  useEffect(() => {
    const fetchBatches = async () => {
      const q = collection(db, "batches");
      const snapshot = await getDocs(q);
      setBatches(snapshot.docs.map((doc) => ({
        id: doc.id,
        name: doc.data().name,
        class: doc.data().class,
        schedule: doc.data().schedule,
        curriculum: doc.data().curriculum,
        location: doc.data().location,
      })));
    };
    fetchBatches();
  }, []);

  // Filtered batches by class
  const filteredBatches = selectedClass
    ? batches.filter((b) => b.class === selectedClass)
    : [];

  // Fetch students when class/batch changes
  useEffect(() => {
    if (!selectedClass || !selectedBatch) {
      setStudents([]);
      return;
    }
    const fetchStudents = async () => {
      setLoading(true);
      const q = query(
        collection(db, "users"),
        where("class", "==", selectedClass),
        where("batchId", "==", selectedBatch)
      );
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map((doc) => ({ ...doc.data(), uid: doc.id })) as User[];
      setStudents(users);
      // Load paid status for selected month
      const paid: { [uid: string]: boolean } = {};
      users.forEach((user) => {
        const paymentPaid = (snapshot.docs.find((d) => d.id === user.uid)?.data().paymentPaid || {}) as Record<string, boolean>;
        paid[user.uid] = paymentPaid[selectedMonth] || false;
      });
      setPaidStatus(paid);
      setLoading(false);
    };
    fetchStudents();
    // eslint-disable-next-line
  }, [selectedClass, selectedBatch, selectedMonth]);

  // Handle checkbox change
  const handlePaidChange = async (uid: string, checked: boolean) => {
    setPaidStatus((prev) => ({ ...prev, [uid]: checked }));
    // Update Firestore: store months and paid details
    const userDoc = doc(db, "users", uid);
    await updateDoc(userDoc, {
      [`paymentPaid.${selectedMonth}`]: checked,
    });
    setToastMsg(`Payment status updated for student (${checked ? "Paid" : "Not Paid"})`);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2500);
  };

  // Get selected batch details
  const selectedBatchObj = filteredBatches.find((b) => b.id === selectedBatch);

  return (
    <div className="w-full max-w-5xl mx-auto mt-8 mb-8">
      <div className="bg-gradient-to-tr from-blue-50 via-indigo-50 to-white rounded-xl shadow-lg p-8">
        <h2 className="text-3xl font-extrabold mb-6 text-blue-900 tracking-tight">Payment Status</h2>
        {/* Summary */}
        <div className="flex flex-wrap gap-4 mb-8 items-center justify-between">
          <div>
            <label className="block mb-1 font-semibold text-gray-700">Select Class</label>
            <select
              className="border-2 border-blue-200 rounded-lg px-4 py-2 w-44 focus:ring-2 focus:ring-blue-400 focus:outline-none shadow-sm"
              value={selectedClass}
              onChange={e => {
                setSelectedClass(e.target.value);
                setSelectedBatch(""); // Reset batch when class changes
              }}
            >
              <option value="">-- Select --</option>
              {classOptions.map(cls => (
                <option key={cls} value={cls}>{cls}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-1 font-semibold text-gray-700">Select Batch</label>
            <select
              className="border-2 border-blue-200 rounded-lg px-4 py-2 w-44 focus:ring-2 focus:ring-blue-400 focus:outline-none shadow-sm"
              value={selectedBatch}
              onChange={e => setSelectedBatch(e.target.value)}
              disabled={!selectedClass}
            >
              <option value="">-- Select --</option>
              {filteredBatches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block mb-1 font-semibold text-gray-700">Select Month</label>
            <select
              className="border-2 border-blue-200 rounded-lg px-4 py-2 w-44 focus:ring-2 focus:ring-blue-400 focus:outline-none shadow-sm"
              value={selectedMonth}
              onChange={e => setSelectedMonth(e.target.value)}
            >
              <option value="">-- Select --</option>
              {monthOptions.map(month => (
                <option key={month} value={month}>{month}</option>
              ))}
            </select>
          </div>
        </div>
        {/* Table */}
        <div className="overflow-x-auto rounded-lg border border-blue-100 shadow-sm">
          {loading ? (
            <div className="p-8 text-center text-lg text-blue-700">Loading students...</div>
          ) : (
            <table className="min-w-full bg-white rounded-lg">
              <thead className="sticky top-0 bg-blue-100 z-10">
                <tr>
                  <th className="px-4 py-3 border-b text-left font-bold text-blue-900">Sl</th>
                  <th className="px-4 py-3 border-b text-left font-bold text-blue-900">Name</th>
                  <th className="px-4 py-3 border-b text-left font-bold text-blue-900">Batch</th>
                  <th className="px-4 py-3 border-b text-left font-bold text-blue-900">Phone Number</th>
                  <th className="px-4 py-3 border-b text-center font-bold text-blue-900">Action</th>
                </tr>
              </thead>
              <tbody>
                {students.map((student, idx) => (
                  <tr key={student.uid} className="odd:bg-blue-50 even:bg-white hover:bg-indigo-50 transition-all">
                    <td className="px-4 py-3 text-center">{idx + 1}</td>
                    <td className="px-4 py-3">{student.name}</td>
                    <td className="px-4 py-3">{student.batchName}</td>
                    <td className="px-4 py-3">{student.phoneNumber || '-'}</td>
                    <td className="px-4 py-3 text-center">
                      <input
                        type="checkbox"
                        checked={!!paidStatus[student.uid]}
                        onChange={e => handlePaidChange(student.uid, e.target.checked)}
                        disabled={!selectedMonth}
                        className="form-checkbox h-5 w-5 text-blue-600 focus:ring-blue-400"
                      />
                      <span className={`ml-3 px-2 py-1 rounded text-xs font-semibold ${paidStatus[student.uid] ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                        {paidStatus[student.uid] ? "Paid" : "Unpaid"}
                      </span>
                    </td>
                  </tr>
                ))}
                {students.length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-8 text-gray-400">No students found for selected class and batch.</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {/* Toast/snackbar */}
      {showToast && (
        <div className="fixed bottom-8 right-8 bg-blue-700 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-fade-in-up">
          {toastMsg}
        </div>
      )}
    </div>
  );
};

export default Payment;
